/**
 * Form slice for the MasterView store.
 *
 * Manages form mode, active record, values, errors, and saving state.
 *
 * @internal
 * @module store/slices/formSlice
 */
import type { StateCreator } from 'zustand';
import type { MasterPatternViewEntity } from '../../types/service.types';
import type { MasterPatternViewDefinition } from '../../types/definition.types';
import type { MasterViewState, FormState } from '../types';
import { getAllFields } from '../../engine/fieldEngine';

/** Seed initial form values: boolean fields default to true (Activo). */
function computeInitialValues(definition: MasterPatternViewDefinition): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const field of getAllFields(definition)) {
    if (field.dataType === 'boolean' || field.inputType === 'boolean') {
      values[field.fieldName] = true;
    }
  }
  return values;
}

export const createFormSlice = <T extends MasterPatternViewEntity>(
  definition: MasterPatternViewDefinition,
  defaultCreateValues?: Record<string, unknown>,
): StateCreator<
  MasterViewState<T>,
  [],
  [],
  FormState<T>
> => (set) => {
  const initialValues = { ...computeInitialValues(definition), ...defaultCreateValues };

  return {
  mode: 'closed',
  activeRecord: null,
  values: {},
  errors: {},
  isDirty: false,
  isSaving: false,
  activeTabKey: '',
  isLoadingRecord: false,
  recordLoadFailed: false,
  initialCreateValues: {},

  openCreate: () =>
    set({
      mode: 'creating',
      activeRecord: null,
      values: { ...initialValues },
      initialCreateValues: { ...initialValues },
      overrides: {},
      revertedFields: [],
      errors: {},
      isDirty: false,
      isSaving: false,
      activeTabKey: '',
      isLoadingRecord: false,
      recordLoadFailed: false,
    }),

  openEdit: (record: T) =>
    set({
      mode: 'editing',
      activeRecord: record,
      values: { ...record },
      // Snapshot the record values as the baseline global values.
      // In company context the consumer applies overrides on top via setValues;
      // globalValues stays as the unmodified global record so the hint can show "Valor global: X".
      globalValues: { ...(record as Record<string, unknown>) },
      overrides: { ...((record as Record<string, unknown>)._overrides as Record<string, unknown> ?? {}) },
      revertedFields: [],
      errors: {},
      isDirty: false,
      isSaving: false,
      activeTabKey: '',
      isLoadingRecord: false,
      recordLoadFailed: false,
    }),

  openView: (record: T) =>
    set({
      mode: 'viewing',
      activeRecord: record,
      values: { ...record },
      errors: {},
      isDirty: false,
      isSaving: false,
      activeTabKey: '',
      isLoadingRecord: false,
      recordLoadFailed: false,
    }),

  closeForm: () =>
    set({
      mode: 'closed',
      activeRecord: null,
      values: {},
      errors: {},
      isDirty: false,
      isSaving: false,
      isLoadingRecord: false,
      recordLoadFailed: false,
    }),

  updateActiveRecord: (record: T) =>
    set({ activeRecord: record }),

  setValues: (values: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) =>
    typeof values === 'function' ? set((state) => ({ values: values(state.values) })) : set({ values }),

  setInitialCreateValues: (values: Record<string, unknown>) =>
    set((state) => ({ initialCreateValues: { ...state.initialCreateValues, ...values } })),

  setErrors: (errors: Record<string, string>) =>
    set({ errors }),

  setIsDirty: (isDirty: boolean) =>
    set({ isDirty }),

  setIsSaving: (isSaving: boolean) =>
    set({ isSaving }),

  setActiveTabKey: (key: string) =>
    set({ activeTabKey: key }),

  setIsLoadingRecord: (isLoadingRecord: boolean) =>
    set({ isLoadingRecord }),

  setRecordLoadFailed: (recordLoadFailed: boolean) =>
    set({ recordLoadFailed }),
  };
};
